/** Iter 233 · Always-on "My Performance" summary card.
 *
 *  Renders three friendly rates so the tab feels valuable even when the user
 *  isn't on a PIP. Sourced from GET /me/performance-rollup.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Target, ListChecks, Clock3 } from "lucide-react";

function Ring({ pct, tone = "primary" }) {
  const r = 32, C = 2 * Math.PI * r;
  const val = pct == null ? 0 : Math.max(0, Math.min(100, pct));
  const stroke = pct == null ? "#94a3b8"
                 : val >= 80 ? "#10b981"
                 : val >= 50 ? "#f59e0b" : "#ef4444";
  return (
    <div className="relative w-[72px] h-[72px] shrink-0">
      <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
        <circle cx="40" cy="40" r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth="8" />
        <circle cx="40" cy="40" r={r} fill="none" stroke={stroke} strokeWidth="8"
                strokeDasharray={C}
                strokeDashoffset={C - (C * val) / 100}
                strokeLinecap="round"
                style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-xs font-bold tabular-nums">
        {pct == null ? "—" : `${Math.round(pct)}%`}
      </div>
    </div>
  );
}

function Sparkline({ points, width = 60, height = 18 }) {
  const vals = (points || []).map((p) => p.value);
  const numeric = vals.filter((v) => v != null);
  if (numeric.length < 2) return null;
  const min = Math.min(...numeric), max = Math.max(...numeric);
  const range = max - min || 1;
  const step = width / Math.max(1, vals.length - 1);
  const pts = vals.map((v, i) => {
    const x = i * step;
    const y = v == null ? height / 2 : height - ((v - min) / range) * height;
    return `${x},${y}`;
  }).join(" ");
  const last = numeric[numeric.length - 1], prev = numeric[numeric.length - 2] ?? last;
  const trending = last >= prev ? "#10b981" : "#ef4444";
  return (
    <svg width={width} height={height} className="mt-1" style={{ overflow: "visible" }}>
      <polyline points={pts} fill="none" stroke={trending} strokeWidth="1.5" />
    </svg>
  );
}

function MetricTile({ Icon, label, pct, hint, trend }) {
  return (
    <div className="border border-border p-3 flex items-center gap-3" data-testid={`perf-rollup-metric-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <Ring pct={pct} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
          <Icon className="w-3 h-3" /> {label}
        </div>
        <div className="text-sm font-medium">
          {pct == null ? <span className="text-muted-foreground italic">Not enough data yet</span> : hint}
        </div>
        <Sparkline points={trend} />
      </div>
    </div>
  );
}

import { useAuth } from "@/lib/auth";

export default function MyPerfRollupCard() {
  const [data, setData] = useState(null);
  const { activeWorkspaceId: wsId } = useAuth();

  useEffect(() => {
    if (!wsId) return;
    api.get("/me/performance-rollup", { params: { workspace_id: wsId } })
      .then((r) => setData(r.data))
      .catch(() => setData({}));
  }, [wsId]);

  if (!data) return null;
  const goals = data.goals_hit_pct;
  const tasks = data.task_completion_pct;
  const ts    = data.timesheet_submission_pct;

  return (
    <div className="border border-border bg-gradient-to-r from-primary/5 to-transparent p-4 space-y-3" data-testid="perf-rollup-card">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Last 30–90 days · your quick vitals</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <MetricTile Icon={Target} label="Goals hit" pct={goals}
          trend={data.trend?.goals}
          hint={goals != null ? `${Math.round(goals)}% of your recent goals completed` : ""} />
        <MetricTile Icon={ListChecks} label="Task completion" pct={tasks}
          trend={data.trend?.tasks}
          hint={tasks != null ? `${Math.round(tasks)}% of tasks assigned in last 30 days are done` : ""} />
        <MetricTile Icon={Clock3} label="Timesheet submission" pct={ts}
          trend={data.trend?.timesheet}
          hint={ts != null ? `${Math.round(ts)}% of last 4 weeks submitted` : ""} />
      </div>
    </div>
  );
}
